import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import * as XLSX from 'xlsx';

import { ItemService } from '../item.service';
import { Item } from '../item.model';
import { Page } from '../../../models/page-wrapper.model';
import { EntityDropdownPickerComponent } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { BrandsService } from '../../brands/brands.service';
import { Brand } from '../../brands/brands.model';
import { SubBrandsService } from '../../brands/sub-brands/sub-brands.service';
import { SubBrand } from '../../brands/sub-brands/sub-brands.model';
import { BusinessService } from '../../business/business.service';
import { Business } from '../../business/business.model';

@Component({
  selector: 'app-list-item',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    EntityDropdownPickerComponent,
  ],
  templateUrl: './list-item.component.html',
  styleUrl: './list-item.component.css',
  providers: [ConfirmationService, MessageService],
})
export class ListItemComponent implements OnInit, OnDestroy {
  @ViewChild('dt') dt!: Table;

  items: Item[] = [];
  totalRecords = 0;
  isLoading = true;
  filterForm!: FormGroup;
  private filterSub?: Subscription;
  brandPickerAdapter!: EntityDropdownPickerAdapter<Brand>;
  subBrandPickerAdapter!: EntityDropdownPickerAdapter<SubBrand>;
  businessPickerAdapter!: EntityDropdownPickerAdapter<Business>;

  constructor(
    private route: ActivatedRoute,
    private itemService: ItemService,
    private brandsService: BrandsService,
    private subBrandsService: SubBrandsService,
    private businessService: BusinessService,
    private router: Router,
    private fb: FormBuilder,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.brandPickerAdapter = this.brandsService.createBrandPickerAdapter();
    this.subBrandPickerAdapter = this.subBrandsService.createSubBrandPickerAdapter();
    this.businessPickerAdapter = this.businessService.createBusinessPickerAdapter();
    this.buildFilterForm();

    this.filterSub = this.filterForm.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        this.dt.reset();
      });

    this.loadItems();
  }

  buildFilterForm(): void {
    this.filterForm = this.fb.group({
      itemNumber: [''],
      itemNameEnglish: [''],
      brand: [null],
      subBrand: [null],
      business: [null],
    });
  }

  onSearch(): void {
    this.dt.reset();
  }

  onReset(): void {
    this.filterForm.reset({
      itemNumber: '',
      itemNameEnglish: '',
      brand: null,
      subBrand: null,
      business: null,
    });
    this.dt.reset();
  }

  loadItems(
    event?: TableLazyLoadEvent,
    isExport: boolean = false,
    callback?: (items: Item[]) => void
  ): void {
    this.isLoading = true;
    const filters = this.filterForm?.value ?? {};
    const size = isExport ? 2000 : event?.rows ?? 10;
    const page = isExport ? 0 : event?.first ? Math.floor(event.first / size) : 0;

    const params: any = {
      page,
      size,
      itemNumber: filters.itemNumber,
      itemNameEnglish: filters.itemNameEnglish,
      'brand.id': filters.brand?.id,
      'subBrand.subBrandId': filters.subBrand?.id,
      'business.id': filters.business?.id,
    };

    Object.keys(params).forEach((key) => {
      if (!params[key]) {
        delete params[key];
      }
    });

    this.itemService.getAllItems(params).subscribe({
      next: (pageResult: Page<Item>) => {
        this.items = pageResult.content || [];
        this.totalRecords = pageResult.totalElements || 0;
        this.isLoading = false;
        if (callback) {
          callback(pageResult.content || []);
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load items.',
        });
        console.error(err);
        this.isLoading = false;
      },
    });
  }

  createItem(): void {
    this.router.navigate(
      ['.', { outlets: { canvasOutlet: ['items', 'create'] } }],
      { relativeTo: this.route.parent?.parent }
    );
  }

  viewItem(id: number): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['items', 'view', id] } },
    ]);
  }

  editItem(id: number): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['items', 'edit', id] } },
    ]);
  }

  deleteItem(item: Item): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this item?',
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-success',
      accept: () => {
        this.itemService.deleteItem(item.itemId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Item deleted successfully.',
            });
            this.loadItems();
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete item.',
            });
            console.error(err);
          },
        });
      },
    });
  }

  exportExcel(): void {
    this.loadItems(undefined, true, (items: Item[]) => {
      const dataToExport = items.map((item) => ({
        'Item No': item.erpItemNumber ?? '-',
        'Item Name English': item.erpItemNameE ?? '-',
        'Item Name Arabic': item.erpItemNameA ?? '-',
        'Item UOM': item.erpItemUom ?? '-',
        'Vendor Catalog': item.erpVendorCatalog ?? '-',
        'Item Status': item.erpItemStatus ?? '-',
        Brand: item.brand?.brandName ?? '-',
        'Sub Brand': item.subBrand?.subBrandName ?? '-',
        Business: item.business?.businessName ?? '-',
        Category: item.category?.name ?? '-',
        'Sub Category': item.subCategory?.name ?? '-',
      }));

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'items');
      XLSX.writeFile(wb, 'Item_Export.xlsx');
    });
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
  }
}
